import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '../components/providers/UserProvider';
import { useToast } from './useToast';
import { StabeonUser } from '../types/user';

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  criteria: (user: StabeonUser) => boolean;
  rewardXP: number;
}

const XP_PER_LEVEL = 100;
const MAX_LEVEL = 100;

export const BADGES: Badge[] = [
  {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Record your first activity',
    icon: '👶',
    criteria: (user) => (user.totalActivityDays || 0) >= 1,
    rewardXP: 10,
  },
  {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: 'Maintain a 7-day streak',
    icon: '🔥',
    criteria: (user) => (user.streak || 0) >= 7,
    rewardXP: 50,
  },
  {
    id: 'monthly_marathoner',
    name: 'Monthly Marathoner',
    description: 'Maintain a 30-day streak',
    icon: '🏃‍♂️',
    criteria: (user) => (user.streak || 0) >= 30,
    rewardXP: 200,
  },
  {
    id: 'explorer',
    name: 'Explorer',
    description: 'Reach level 5',
    icon: '🧭',
    criteria: (user) => (user.level || 1) >= 5,
    rewardXP: 100,
  },
  {
    id: 'adventurer',
    name: 'Adventurer',
    description: 'Reach level 15',
    icon: '🗺️',
    criteria: (user) => (user.level || 1) >= 15,
    rewardXP: 300,
  },
  {
    id: 'globetrotter',
    name: 'Globetrotter',
    description: 'Reach level 30',
    icon: '🌍',
    criteria: (user) => (user.level || 1) >= 30,
    rewardXP: 500,
  },
  {
    id: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'Be active 10 different days',
    icon: '🦋',
    criteria: (user) => (user.totalActivityDays || 0) >= 10,
    rewardXP: 75,
  },
  {
    id: 'fitness_fanatic',
    name: 'Fitness Fanatic',
    description: 'Be active 25 different days',
    icon: '💪',
    criteria: (user) => (user.totalActivityDays || 0) >= 25,
    rewardXP: 75,
  },
];

export const calculateLevel = (xp: number): number => {
  return Math.min(Math.floor(Math.sqrt(xp / XP_PER_LEVEL)) + 1, MAX_LEVEL);
};

export const xpForNextLevel = (level: number): number => {
  return XP_PER_LEVEL * Math.pow(level - 1, 2);
};

// Local date string (timezone-aware) instead of UTC ISO
function getLocalDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const useGamification = () => {
  const { user, updateUser } = useUser();
  const { show } = useToast();
  const [levelProgress, setLevelProgress] = useState({ current: 0, next: 0, percentage: 0 });

  // Refs to prevent infinite update loops
  const badgeCheckRef = useRef(false);
  const levelCheckRef = useRef(false);

  // Calculate level progress (read-only, no side effects)
  useEffect(() => {
    if (!user) return;
    const currentLevelXP = xpForNextLevel(user.level || 1);
    const nextLevelXP = xpForNextLevel((user.level || 1) + 1);
    const progressInLevel = (user.experience || 0) - currentLevelXP;
    const levelRange = nextLevelXP - currentLevelXP;
    const percentage = levelRange > 0 ? (progressInLevel / levelRange) * 100 : 0;

    setLevelProgress({
      current: user.level || 1,
      next: (user.level || 1) + 1,
      percentage: Math.min(100, Math.max(0, percentage)),
    });
  }, [user?.level, user?.experience]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check for new badges — guarded to run only once per change
  useEffect(() => {
    if (!user || badgeCheckRef.current) return;

    const currentBadges = user.badges || [];
    const newBadges = BADGES.filter(
      badge => !currentBadges.includes(badge.id) && badge.criteria(user)
    );

    if (newBadges.length > 0) {
      badgeCheckRef.current = true;
      const totalRewardXP = newBadges.reduce((sum, badge) => sum + badge.rewardXP, 0);
      updateUser({
        experience: (user.experience || 0) + totalRewardXP,
        badges: [...currentBadges, ...newBadges.map(b => b.id)],
      });
      newBadges.forEach(badge => {
        show(`${badge.icon} Badge earned: ${badge.name}!`, 'success');
      });
      // Allow next check after a short delay
      setTimeout(() => { badgeCheckRef.current = false; }, 1000);
    }
  }, [user?.badges?.length, user?.streak, user?.totalActivityDays, user?.level]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update level when experience changes — guarded
  useEffect(() => {
    if (!user || levelCheckRef.current) return;
    const newLevel = calculateLevel(user.experience || 0);
    if (newLevel !== (user.level || 1)) {
      levelCheckRef.current = true;
      updateUser({ level: newLevel });
      if (newLevel > (user.level || 1)) {
        show(`Level up! You're now level ${newLevel}`, 'success');
      }
      setTimeout(() => { levelCheckRef.current = false; }, 1000);
    }
  }, [user?.experience]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update streak based on activity
  const updateStreak = useCallback((activityDate: string) => {
    if (!user) return;

    const today = getLocalDateString();
    const lastDate = user.lastActivityDate;

    let newStreak = user.streak || 0;
    let newTotalDays = user.totalActivityDays || 0;

    if (lastDate) {
      const last = new Date(lastDate + 'T00:00:00'); // local midnight
      const todayDate = new Date(today + 'T00:00:00');
      const diffDays = Math.round((todayDate.getTime() - last.getTime()) / (86400 * 1000));

      if (diffDays === 1) {
        newStreak += 1;
      } else if (diffDays > 1) {
        newStreak = 1;
      }
      // diffDays === 0: same day, streak unchanged
    } else {
      newStreak = 1;
    }

    if (lastDate !== today) {
      newTotalDays += 1;
    }

    updateUser({
      streak: newStreak,
      lastActivityDate: today,
      totalActivityDays: newTotalDays,
    });
  }, [user, updateUser]);

  const addExperience = useCallback((xp: number) => {
    if (!user) return;
    updateUser({ experience: (user.experience || 0) + xp });
  }, [user, updateUser]);

  return {
    badges: user?.badges || [],
    streak: user?.streak || 0,
    level: user?.level || 1,
    experience: user?.experience || 0,
    totalActivityDays: user?.totalActivityDays || 0,
    lastActivityDate: user?.lastActivityDate,
    levelProgress,
    xpForNextLevel: xpForNextLevel(user?.level || 1),
    updateStreak,
    addExperience,
    BADGES,
    getBadgeById: (id: string) => BADGES.find(b => b.id === id),
    hasBadge: (id: string) => (user?.badges || []).includes(id),
  };
};
